<script setup>
// The event's page asks a visitor who they are: volunteers have no accounts, they look themselves up by name.
// Once one said "that's me", the page remembers it in this browser and greets them with their next shifts.
import { onMounted, onUnmounted, ref } from 'vue'
import { get } from '@rails/request.js'
import { useI18n } from 'vue-i18n'
import { forgetVolunteer, readRememberedVolunteer, rememberVolunteer } from '../modules/remembered-volunteer'

const props = defineProps({
  eventId: String,
  searchUrl: String,
  // With `__id__` standing in for the volunteer
  upcomingUrl: String,
})

const SEARCH_MIN_LENGTH = 2
const SEARCH_DELAY = 250

const { t } = useI18n()

const query = ref('')
const results = ref(null)
const searching = ref(false)
// The remembered volunteer with the next shifts, once loaded
const volunteer = ref(null)
const loading = ref(false)

let searchTimer = null
// Answers of earlier searches arriving late must not replace those of the latest one
let searchRun = 0

async function loadUpcoming(id) {
  loading.value = true
  try {
    const response = await get(props.upcomingUrl.replace('__id__', id), { responseKind: 'json' })
    if (response.ok) {
      volunteer.value = await response.json
    } else if (response.statusCode === 404) {
      // Removed from the event since: nobody to greet any more
      forgetVolunteer(props.eventId)
    }
  } finally {
    loading.value = false
  }
}

async function search(term) {
  const run = ++searchRun
  searching.value = true
  try {
    const response = await get(props.searchUrl, { query: { q: term }, responseKind: 'json' })
    if (run !== searchRun) return

    results.value = response.ok ? await response.json : []
  } finally {
    if (run === searchRun) searching.value = false
  }
}

function onInput() {
  clearTimeout(searchTimer)
  const term = query.value.trim()
  if (term.length < SEARCH_MIN_LENGTH) {
    searchRun++
    results.value = null
    searching.value = false
    return
  }
  searchTimer = setTimeout(() => search(term), SEARCH_DELAY)
}

function choose(result) {
  rememberVolunteer(props.eventId, result)
  loadUpcoming(result.id)
}

function notMe() {
  forgetVolunteer(props.eventId)
  volunteer.value = null
  query.value = ''
  results.value = null
}

onMounted(() => {
  const remembered = readRememberedVolunteer(props.eventId)
  if (remembered) loadUpcoming(remembered.id)
})

onUnmounted(() => clearTimeout(searchTimer))
</script>

<template>
  <div class="card h-100 event-volunteer-finder">
    <template v-if="volunteer">
      <div class="card-header d-flex justify-content-between align-items-center gap-2">
        <span class="fw-semibold">
          <span class="bi bi-person-badge me-1"></span>
          {{ t('event_volunteer_finder.greeting', { name: volunteer.name }) }}
        </span>
        <button type="button" class="btn btn-sm btn-link p-0" @click="notMe">
          {{ t('event_volunteer_finder.not_me') }}
        </button>
      </div>
      <ul class="list-group list-group-flush">
        <li v-for="shift in volunteer.shifts" :key="shift.key" class="list-group-item d-flex gap-2 align-items-start">
          <span class="text-nowrap text-body-secondary small pt-1" style="min-width: 7.5rem">{{ shift.time }}</span>
          <span>
            <span :style="{ color: shift.color }" class="bi-icon bi-circle-fill me-1"></span>
            <a :href="shift.positionUrl">{{ shift.positionName }}</a>
            <span v-if="shift.current" class="badge text-bg-success ms-1">{{ t('event_volunteer_finder.now') }}</span>
            <span v-if="shift.divisionName" class="small text-body-secondary d-block">
              {{ t('event_volunteer_finder.whole_division', { division: shift.divisionName }) }}
            </span>
            <span v-if="shift.comment" class="small d-block">{{ shift.comment }}</span>
          </span>
        </li>
        <li v-if="!volunteer.shifts.length" class="list-group-item text-body-secondary">
          {{ t('event_volunteer_finder.no_shifts') }}
        </li>
      </ul>
      <div class="card-footer d-flex flex-wrap gap-2 align-items-center">
        <a :href="volunteer.url" class="btn btn-sm btn-primary">
          {{ t('event_volunteer_finder.all_shifts') }}
        </a>
        <a v-if="volunteer.calendarUrl" :href="volunteer.calendarUrl" class="btn btn-sm btn-outline-secondary">
          <span class="bi bi-download me-1"></span>
          {{ t('event_volunteer_finder.calendar') }}
        </a>
        <vue-volunteer-subscription :volunteer-id="volunteer.id" :subscription-url="volunteer.subscriptionUrl" />
      </div>
    </template>

    <template v-else>
      <div class="card-header fw-semibold">
        <span class="bi bi-search me-1"></span>
        {{ t('event_volunteer_finder.title') }}
      </div>
      <div class="card-body">
        <label :for="`event-volunteer-finder-${eventId}`" class="form-label">{{ t('event_volunteer_finder.label') }}</label>
        <input
          :id="`event-volunteer-finder-${eventId}`"
          v-model="query"
          type="search"
          class="form-control form-control-lg"
          autocomplete="off"
          :placeholder="t('event_volunteer_finder.placeholder')"
          :disabled="loading"
          @input="onInput"
        >
        <div class="form-text">{{ t('event_volunteer_finder.hint') }}</div>
      </div>
      <ul v-if="results" class="list-group list-group-flush" aria-live="polite">
        <li v-for="result in results" :key="result.id" class="list-group-item d-flex justify-content-between align-items-center gap-2">
          <a :href="result.url" class="text-decoration-none">
            <span :style="{ color: result.color }" class="bi-icon bi-circle-fill me-1"></span>
            <span>{{ result.name }}</span>
            <span class="small text-body-secondary ms-1">{{ result.divisionName }}</span>
          </a>
          <button type="button" class="btn btn-sm btn-outline-primary text-nowrap" @click="choose(result)">
            {{ t('event_volunteer_finder.choose') }}
          </button>
        </li>
        <li v-if="!results.length && !searching" class="list-group-item text-body-secondary">
          {{ t('event_volunteer_finder.no_results') }}
        </li>
      </ul>
    </template>
  </div>
</template>
